import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Label,
} from "@learn-me/ui";
import { api } from "@/lib/api";
import toast from "react-hot-toast";
import {
  Loader2,
  Mail,
  User,
  Lock,
  XCircle,
  BookOpen,
  Package,
  AlertTriangle,
} from "lucide-react";

interface InvitationData {
  email: string;
  firstName: string | null;
  lastName: string | null;
  autoEnrollCourse: {
    id: string;
    title: string;
    thumbnailUrl: string | null;
  } | null;
  autoEnrollBundle: {
    id: string;
    name: string;
    thumbnailUrl: string | null;
  } | null;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

async function validateToken(token: string): Promise<InvitationData> {
  const response = await api.get<ApiResponse<InvitationData>>(
    `/auth/invitation/${token}`,
  );
  return response.data.data!;
}

interface AcceptInvitationResponse {
  user: { id: string; email: string };
  checkout: {
    type: "course" | "bundle";
    id: string;
    slug: string;
    title: string;
  } | null;
  message: string;
}

async function acceptInvitation(
  token: string,
  data: { firstName: string; lastName: string; password: string },
): Promise<AcceptInvitationResponse> {
  const response = await api.post<ApiResponse<AcceptInvitationResponse>>(
    `/auth/invitation/${token}/accept`,
    data,
  );
  return response.data.data!;
}

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const {
    data: invitation,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["invitation-validate", token],
    queryFn: () => validateToken(token!),
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (invitation) {
      if (invitation.firstName) setFirstName(invitation.firstName);
      if (invitation.lastName) setLastName(invitation.lastName);
    }
  }, [invitation]);

  const acceptMutation = useMutation({
    mutationFn: (data: {
      firstName: string;
      lastName: string;
      password: string;
    }) => acceptInvitation(token!, data),
    onSuccess: (result) => {
      if (result.checkout) {
        // Si hay curso/paquete, redirigir al login con info para ir al checkout despues
        toast.success(
          `¡Cuenta creada! Inicia sesion para completar tu inscripcion a "${result.checkout.title}".`,
        );
        navigate("/login", {
          state: {
            email: invitation?.email,
            redirectTo:
              result.checkout.type === "course"
                ? `/checkout/${result.checkout.id}`
                : `/bundles/${result.checkout.slug}`,
          },
        });
      } else {
        toast.success("¡Cuenta creada exitosamente! Ya puedes iniciar sesion.");
        navigate("/login", { state: { email: invitation?.email } });
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al crear la cuenta");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    if (password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }

    if (!acceptedTerms) {
      toast.error("Debes aceptar los terminos y condiciones");
      return;
    }

    acceptMutation.mutate({ firstName, lastName, password });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Validando invitacion...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !invitation) {
    const errorMessage =
      (error as { message?: string })?.message ||
      "Esta invitacion no es valida o ha expirado.";

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invitacion no valida</h2>
            <p className="text-muted-foreground mb-6">{errorMessage}</p>
            <Link to="/login">
              <Button variant="outline">Ir al inicio de sesion</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">¡Has sido invitado!</h1>
          <p className="text-muted-foreground">
            Completa tu registro para unirte a la plataforma.
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Course/Bundle info */}
          {(invitation.autoEnrollCourse || invitation.autoEnrollBundle) && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <BookOpen className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">
                    Tu invitacion incluye acceso a:
                  </p>
                  {invitation.autoEnrollCourse && (
                    <div className="flex items-center gap-2 mt-2">
                      <BookOpen className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-700">
                        {invitation.autoEnrollCourse.title}
                      </span>
                    </div>
                  )}
                  {invitation.autoEnrollBundle && (
                    <div className="flex items-center gap-2 mt-2">
                      <Package className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-700">
                        {invitation.autoEnrollBundle.name}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-blue-600 mt-2">
                    Despues de crear tu cuenta, seras redirigido para completar
                    tu inscripcion.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Email display */}
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Correo electronico</p>
            <p className="font-medium">{invitation.email}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Nombre *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="firstName"
                    className="pl-10"
                    placeholder="Juan"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Apellido *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="lastName"
                    className="pl-10"
                    placeholder="Perez"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  className="pl-10"
                  placeholder="Minimo 8 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar contraseña *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  className="pl-10"
                  placeholder="Repite tu contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <div className="flex items-center gap-1 text-destructive text-sm">
                  <AlertTriangle className="h-3 w-3" />
                  <span>Las contraseñas no coinciden</span>
                </div>
              )}
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="terms" className="text-sm text-muted-foreground">
                Acepto los{" "}
                <Link to="/terms" className="text-primary hover:underline">
                  terminos y condiciones
                </Link>{" "}
                y la{" "}
                <Link to="/privacy" className="text-primary hover:underline">
                  politica de privacidad
                </Link>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={
                acceptMutation.isPending ||
                !firstName ||
                !lastName ||
                !password ||
                password !== confirmPassword ||
                !acceptedTerms
              }
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creando cuenta...
                </>
              ) : (
                "Crear mi cuenta"
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            ¿Ya tienes una cuenta?{" "}
            <Link to="/login" className="text-primary hover:underline">
              Inicia sesion
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
